<template>
    <div>
        <div class="flex gap-2 mb-3 lg:mb-4">
            <div v-if="!props.disabled" class="grow"
                data-test="value-wrapper">
                <select-group v-if="props.groupSelect"
                    :error="displayFirstError(validate.value) || displayFirstError(validate.values)"
                    placeholder="group"
                    v-model="value"
                    :orgUnitId="props.orgUnitId"
                    :statuses="GROUP_STATUSES_CONFIGURABLE"
                    @option:selected="add()">
                </select-group>
                <select-org-unit v-else-if="props.orgUnitSelect"
                    :error="displayFirstError(validate.value) || displayFirstError(validate.values)"
                    placeholder="organizational unit"
                    v-model="value"
                    :reduce="(orgUnit) => String(orgUnit.id)"
                    noOrgUnitOption
                    @option:selected="addOrgUnit($event)">
                </select-org-unit>
                <vx-input v-else
                    placeholder="enter a value"
                    v-model="value"
                    @keypress.enter.prevent="add()"
                    @blur="handleBlur()"
                    :error="displayFirstError(validate.value) || displayFirstError(validate.values)">
                    <template v-slot:right>
                        <vx-button @click.prevent="add()"
                            data-test="add"
                            aria-label="Add value"
                            color="transparent"
                            type="button"
                            class="-mr-4">
                            <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </template>
                </vx-input>
            </div>
        </div>

        <div class="pl-4">
            <vx-list-animated v-model="values"
                :removeable="!props.disabled"
                :getLabel="props.orgUnitSelect ? orgUnitLabel : undefined"></vx-list-animated>
        </div>
    </div>
</template>

<script setup>
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxInput, VxListAnimated } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, minLength, required, requiredIf } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { csvInjection, customAttributeValue, displayFirstError } from '~/utils/validation';
import SelectGroup from '~/components/general/SelectGroup.vue';
import SelectOrgUnit from '~/components/general/SelectOrgUnit.vue';
import { fetchOrgUnitsByIds, orgUnitName } from '~/components/general/fetchOrgUnitsByIds';
import { GROUP_STATUSES_CONFIGURABLE } from '~/constants/groupStatuses';

const teamId = Spark.state.currentTeam.id;

const value = ref('');

const values = defineModel({
    type: Array,
    required: true,
    default: () => ([])
});

const props = defineProps({
    groupSelect: {
        type: Boolean,
        default: false,
    },
    orgUnitSelect: {
        type: Boolean,
        default: false,
    },
    orgUnitId: {
        type: Number,
    },
    disabled: Boolean,
})

const orgUnitLabels = ref(new Map());

watch(
    () => (props.orgUnitSelect ? values.value.filter((id) => !orgUnitLabels.value.has(id)) : []),
    async (ids) => {
        if (ids.length === 0) {
            return;
        }

        ids.forEach((id) => orgUnitLabels.value.set(id, ''));

        const orgUnits = await fetchOrgUnitsByIds(teamId, ids);

        ids.forEach((id) => orgUnitLabels.value.set(id, orgUnitName(orgUnits, id)));
    },
    { immediate: true }
);

const orgUnitLabel = (id) => orgUnitLabels.value.get(id);

const validate = useVuelidate({
    value: {
        requiredIf: helpers.withMessage('Value is required', requiredIf(() => values.value.length === 0)),
        customAttributeValue: customAttributeValue,
        csvInjection: csvInjection,
    },
    values: {
        required: helpers.withMessage('You need to add at least one value', required),
        minLength: minLength(1),
        maxLength: maxLength(50),
    }
}, computed(() => ({ value: value, values: values })));

const isAutoSubmitting = ref(false);

const handleBlur = async () => {
    if (isAutoSubmitting.value || !value.value) {
        return;
    }

    isAutoSubmitting.value = true;

    try {
        await add();
    } finally {
        isAutoSubmitting.value = false;
    }
};

const add = async () => {
    const isValid = await validate.value.value.$validate();
    if (!isValid) {
        return;
    }
    if (values.value.find(existingValue => existingValue === value.value)){
        value.value = '';
        return;
    }
    if(!value.value.length){
        return;
    }

    values.value.push(value.value);
    value.value = '';
}

const addOrgUnit = (orgUnit) => {
    orgUnitLabels.value.set(String(orgUnit.id), orgUnit.name);
    add();
};

</script>
